import { FC, useCallback } from 'react'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { useTranslation } from 'next-i18next/pages'
import { Button } from '@corratech/pylot-ui'
import { UseFormGetValues } from 'react-hook-form'
import {
    Wishlist,
    ConfigurableWishlistItem,
    CartItemInput
} from '@pylot-data/pylotschema'
import { useAddToCartFromWishlist } from '../../Hooks/use-add-to-cart-from-wishlist'
import { getUidForConfigProducts } from '../../utils/getUidForConfigProducts'
import s from '@pagestyles/Wishlist.module.scss'
import cn from 'classnames'

export interface WishlistAddItemArgs {
    cartItems: Array<CartItemInput>
    removeItems: Array<{ id: string; productId: string }>
    itemName?: string
}

type WishlistStateArray = {
    selected: string[]
}

export type Props = {
    wishlist?: Wishlist
    getValues: UseFormGetValues<any>
    buttonLabel?: string
    item?: WishlistAddItemArgs
    index?: number
    isValid?: boolean
    wishlistState: WishlistStateArray
    addSelectedButtonActive?: boolean
    resetSelected: () => void
}

export const AddProductsToCart: FC<Props> = ({
    wishlist,
    item,
    buttonLabel,
    getValues,
    index,
    isValid = true,
    wishlistState,
    addSelectedButtonActive = false,
    resetSelected
}) => {
    const { t } = useTranslation(['wishlist'])
    const { openToast } = useUI()
    const { addToCartFromWishlist, isLoading } = useAddToCartFromWishlist()
    const hasSelectedItems = wishlistState?.selected.length > 0

    const getWishlistAddItemArgs = useCallback(
        (wishlistState?: WishlistStateArray): WishlistAddItemArgs => {
            const itemsArray: WishlistAddItemArgs = {
                cartItems: [],
                removeItems: [],
                itemName: ''
            }

            const filteredItems =
                wishlist &&
                wishlist!.items_v2?.items.filter((item) =>
                    wishlistState?.selected.includes(String(item?.id))
                )

            const items = wishlistState
                ? (filteredItems as Array<ConfigurableWishlistItem>)
                : (wishlist!.items_v2?.items as Array<ConfigurableWishlistItem>)

            //get all the products for adding to cart from wishlist
            items?.forEach((wishlistItem, key) => {
                itemsArray.cartItems.push({
                    sku: wishlistItem?.product?.sku!,
                    selected_options: getUidForConfigProducts({
                        product: wishlistItem?.product,
                        options: wishlistItem?.configurable_options
                    }),
                    quantity:
                        getValues(String(key))?.quantity ||
                        wishlistItem?.quantity
                })
                itemsArray.itemName = wishlistItem?.product?.name!

                itemsArray.removeItems.push({
                    id: wishlistItem?.id,
                    productId: String(wishlistItem?.product?.id)
                })
            })

            return itemsArray
        },
        [wishlist?.id, wishlist?.items_v2]
    )

    const handleAddToCart = (wishlistState?: WishlistStateArray) => {
        try {
            const addItemsInput = item
                ? {
                      ...item,
                      ...{
                          cartItems: [
                              {
                                  ...item.cartItems[0]
                              }
                          ]
                      }
                  }
                : getWishlistAddItemArgs(wishlistState)

            addToCartFromWishlist(
                addItemsInput?.cartItems,
                {
                    wishlistId: wishlist!.id!,
                    wishlistItemsIds: addItemsInput?.removeItems
                },
                addItemsInput?.itemName
            )
            resetSelected()
        } catch (e) {
            openToast(
                t('Something went wrong. Please try again'),
                ToastType.Danger
            )
        }
    }

    return (
        <div>
            <Button
                type="button"
                variant="primary"
                className={cn('btn-sm', s['add-to-cart-btn'])}
                loading={isLoading}
                disabled={isLoading || !isValid}
                onClick={() => handleAddToCart()}
            >
                {buttonLabel}
            </Button>
            {addSelectedButtonActive && (
                <Button
                    type="button"
                    variant="secondary"
                    className={cn('btn-sm', s['add-to-cart-btn'])}
                    loading={isLoading}
                    disabled={isLoading || !isValid || !hasSelectedItems}
                    onClick={() => handleAddToCart(wishlistState)}
                >
                    {t('wishlist|Add selected')}
                </Button>
            )}
        </div>
    )
}
